import * as mobx from 'mobx';
import * as mobxReact from 'mobx-react';
import * as React from 'react';
import { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';

import { IAttendeeOrganizationDTO } from '../../../../../api/attendees.dao';
import { IOperatorDetailsDTO } from '../../../../../api/rooms.dao';
import { MeetingConnectionsAggregateRoot } from '../../../../../modules/connections/meeting/meeting-connections.aggregate-root';
import { hasMeetingRoleConnection } from '../../../../../modules/connections/const';
import { OrganizationRole } from '../../../../../modules/connections/types';
import { MultiSelectFilterType } from '../../../../global/multi-select/make-multi-select-in-popover';
import { OrganizationMultiSelectButton } from '../../../../global/multi-select/organization-multi-select-button';
import { OrganizationConnection } from './organization-connection';
import { ConnectionType, IConnectionDetailsDTO, IConnectionGroupDTO, onChangeConnectionEntity } from '../../../../../api/connections.dao';
import { ConnectionVersion } from '../../../../../constants/common.constants';
import { UNIQUE_TEXT } from '../_meeting-connections-form/meeting-connections-form.texts';
import cn from 'classnames';
import { CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS } from '../../../../../tracking-service/tracking-values';
import { ChildConnectionsViewModel } from '../_meeting-connections-form/child-connections/child-connections.viewmodel';
import { ConnectionDropdownPresentation } from '../../shared/_connection-dropdown/connection-dropdown.presentation';
import { Permission } from '../../../../../api/roles.dao';
import { hasPermission } from '../../../../../infrastructure/utils/has-permissions';

export type PropsType = {
    entity: MeetingConnectionsAggregateRoot;
    removeOrganization?: (id: number) => void;
    removeAttendee?: (id: number) => void;
    roleName: string;
    role: OrganizationRole;
    attendees: (IAttendeeOrganizationDTO | IOperatorDetailsDTO)[];
    // eslint-disable-next-line @typescript-eslint/naming-convention
    ActionButton?: () => ReactElement;
    onClick?: () => void;
    webinarRegistrantsCount?: number;
    editConnection?: (connection: IConnectionDetailsDTO) => void;
    meetingConnections: IConnectionDetailsDTO[];
    connectionGroups?: IConnectionGroupDTO[];
    filteredMeetingConnections: IConnectionDetailsDTO[];
    onChangeMeetingConnection?: (
        entity: onChangeConnectionEntity,
        connectionId: number | null,
        changePresenterOrHost?: boolean
        ) => void;
        isRoleRegistrant?: boolean;
    knovioReplayConnection: IConnectionDetailsDTO;
    disabledDropDownConnection: boolean;
    connectionVersion?: ConnectionVersion;
    handleTrackChildCopyBtn?: (event: CONNECTION_DETAILS_POPOVER_TRACKING_EVENTS, type: string) => void;
    childConnectionsVM: ChildConnectionsViewModel;
};

export const ParticipantConnectionList: React.FC<PropsType> = mobxReact.observer(
    ({
        roleName,
        entity,
        role,
        attendees,
        ActionButton,
        onClick,
        removeOrganization,
        removeAttendee,
        webinarRegistrantsCount,
        editConnection,
        meetingConnections,
        onChangeMeetingConnection,
        filteredMeetingConnections,
        isRoleRegistrant = false,
        knovioReplayConnection,
        disabledDropDownConnection,
        connectionVersion,
        connectionGroups = [],
        handleTrackChildCopyBtn,
        childConnectionsVM
    }: PropsType): JSX.Element => {

        const handleOnChange = mobx.action((orgAttendees: IAttendeeOrganizationDTO[]): void => {
            entity.setOrganizationsToRole(role, orgAttendees);
        });

        const derivedConnection = entity.getDerivedConnectionForRole(role);
        const meetingRoleAssignedConnectionId = entity.hasMeetingRoleAssignedConnection(role)
            ? entity.getMeetingRoleConnectionId(role)
            : null;
        const meetingRoleDropdownDerivedConnection = entity.getDerivedConnectionForMeetingRoleDropdown(role);
        const canEditMeetingRoleConnection = hasPermission(Permission.CONNECTIONS_MANAGEMENT);
        const shouldShowMeetingRoleDropdown = hasMeetingRoleConnection(role);
        const hasPanelistLink = meetingConnections.some(c =>
            c.type === ConnectionType.Webinar &&
            c.childConnections?.some(child => child.type === ConnectionType.PanelistLink)
        );

        const hasAutoSyncOrRegistrationEnabled = meetingConnections.some(c => c.autoSyncEnabled || c.autoRegistrationEnabled);
        const isV2Connection = connectionVersion === ConnectionVersion.V2;
        const hasWebinarRegistrants = webinarRegistrantsCount !== 0;
        const isV2AndAutoSyncEnabled = isV2Connection && hasAutoSyncOrRegistrationEnabled;
        const isRegistrantNotRoleOrHasWebinarRegistrants = !isRoleRegistrant || hasWebinarRegistrants;
        const isV2AndHasPanelistLink = isV2Connection && hasPanelistLink;
        const shouldRenderUniqueSection = (isV2AndAutoSyncEnabled && isRegistrantNotRoleOrHasWebinarRegistrants) || isV2AndHasPanelistLink;

        const uniqueColumnWithLeftMargin = shouldRenderUniqueSection && !isRoleRegistrant;

        const handleMeetingRoleConnectionChange = mobx.action((connectionId: number | null): void => {
            if (!canEditMeetingRoleConnection) {
                return;
            }
            if (connectionId === null) {
                entity.removeMeetingRoleConnection(role);
            } else {
                entity.assignMeetingRoleConnection(role, connectionId);
            }
        });


        return (
            <div className='attendee-list'>
                <div className='attendee-list__row'>
                    <div className='attendee-list__label'>
                        {webinarRegistrantsCount}
                        <FormattedMessage
                            id={`oe.input.${roleName}`}
                            description='Input label'
                            defaultMessage={roleName}
                        />
                    </div>
                    {shouldRenderUniqueSection &&
                        <div
                            className={cn('organization__unique-column', {
                                'organization__unique-column--left-margin': uniqueColumnWithLeftMargin
                            })}
                        >
                            <FormattedMessage {...UNIQUE_TEXT} />
                        </div>
                    }
                    {shouldShowMeetingRoleDropdown &&
                        <div className={cn('attendee-list__label', {
                            'attendee-list__label--with-role-connection': shouldShowMeetingRoleDropdown
                        })}>
                            <ConnectionDropdownPresentation
                                meetingConnections={filteredMeetingConnections}
                                connectionGroups={connectionGroups}
                                assignedConnectionId={meetingRoleAssignedConnectionId}
                                derivedConnection={meetingRoleDropdownDerivedConnection}
                                isLabelVisibleCondition={Boolean(
                                    knovioReplayConnection ||
                                    meetingRoleAssignedConnectionId ||
                                    meetingRoleDropdownDerivedConnection
                                )}
                                onChange={handleMeetingRoleConnectionChange}
                                knovioReplayConnection={knovioReplayConnection}
                                disabledDropDownConnection={disabledDropDownConnection || !canEditMeetingRoleConnection}
                                connectionVersion={connectionVersion}
                            />
                        </div>
                    }
                </div>
                {entity.getOrganizationsByRole(role)
                    ?.slice()
                    .sort((a, b) => a.getProps().name.localeCompare(b.getProps().name))
                    .map(orgEntity => (
                        <OrganizationConnection
                            name={orgEntity.getProps().name}
                            meetingConnections={meetingConnections}
                            filteredMeetingConnections={filteredMeetingConnections}
                            derivedConnection={derivedConnection}
                            entity={orgEntity}
                            key={orgEntity.getId()}
                            removeAttendee={removeAttendee}
                            removeOrganization={removeOrganization}
                            editConnection={editConnection}
                            onChangeMeetingConnection={onChangeMeetingConnection}
                            isRoleRegistrant={isRoleRegistrant}
                            knovioReplayConnection={knovioReplayConnection}
                            disabledDropDownConnection={disabledDropDownConnection}
                            connectionVersion={connectionVersion}
                            connectionGroups={connectionGroups}
                            shouldRenderUniqueSection={shouldRenderUniqueSection}
                            meetingParentConnections={entity.getParentConnectionList()}
                            handleTrackChildCopyBtn={handleTrackChildCopyBtn}
                            childConnectionsVM={childConnectionsVM}
                            getConnectionsListFromGroup={entity.getConnectionsListFromGroup}
                        />
                    ))
                }
                {!isRoleRegistrant && <OrganizationMultiSelectButton
                    className='attendee-list__add-button'
                    items={attendees}
                    value={entity.getOrganizationListByRole(role)}
                    disabled={entity.getMeetingOrganizationAttendees()}
                    keyProperty='organizationId'
                    nameProperty='name'
                    onChange={handleOnChange}
                    onClick={onClick}
                    text={<FormattedMessage
                        id='oe.attendee.add'
                        description='Button text'
                        defaultMessage='+ Add {name}'
                        values={{
                            name: roleName
                        }}
                    />}
                    filterType={MultiSelectFilterType.Group}
                    applyButtonText='Add'
                    resetButtonText='Cancel'
                    searchPlaceholder='Search'
                    ActionButton={ActionButton}
                />}
            </div>
        );
    });

